import { Component, ElementRef, OnInit, AfterViewInit, OnDestroy, inject, input, viewChild } from "@angular/core";
import { ChangeDetectionStrategy, NgZone } from "@angular/core";
import { tap } from "rxjs/operators";

import { Subscription, timer } from "rxjs";

@Component({
    selector: "app-analog-clock",
    template: "<canvas #canvas></canvas>",
    styles: [],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true
})
export class AnalogClockComponent implements OnDestroy, AfterViewInit {
	private ngZone = inject(NgZone);

	readonly canvasRef = viewChild<ElementRef>("canvas");
	public width: number
	public height: number;
	canvasContext: CanvasRenderingContext2D;
	subscription: Subscription;

	constructor() {}

	ngAfterViewInit() {
		const canvasEl: HTMLCanvasElement = this.canvasRef().nativeElement;
		const clockSize = 432;
		this.width = this.height = clockSize;
		canvasEl.width = this.width;
		canvasEl.height = this.height;
		const radius = this.width / 2;
		const innerRadius = radius * 0.9;
		this.canvasContext = canvasEl.getContext("2d");
		this.canvasContext.translate(radius, radius);

		this.ngZone.runOutsideAngular(() => this.draw(innerRadius));
	}

	ngOnDestroy() {
		this.subscription.unsubscribe();
	}

	draw(innerRadius: number) {
		this.subscription = timer(0, 1000)
			.pipe(
				tap((t) => {
					this.clearCanvas();
					this.drawFace(this.canvasContext, innerRadius);
					this.drawNumbers(this.canvasContext, innerRadius);
					this.drawTime(this.canvasContext, innerRadius);
				})
			)
			.subscribe();
	}
	clearCanvas() {
		const canvasEl: HTMLCanvasElement = this.canvasRef().nativeElement;
		this.canvasContext.save(); // Save current context state
		this.canvasContext.setTransform(1, 0, 0, 1, 0, 0); // Reset transformations
		this.canvasContext.clearRect(0, 0, canvasEl.width, canvasEl.height); // Clear everything
		this.canvasContext.restore(); // Restore context to previous state
	}

	drawFace(ctx, radius) {
		ctx.beginPath();
		ctx.arc(0, 0, radius, 0, 2 * Math.PI);
		ctx.fillStyle = "transparent"; //TODO
		ctx.fill();
		const grad = ctx.createRadialGradient(0, 0, radius * 0.95, 0, 0, radius * 1.05);
		grad.addColorStop(0, "#333");
		grad.addColorStop(0.5, "white");
		grad.addColorStop(1, "#333");
		ctx.strokeStyle = grad;
		ctx.lineWidth = radius * 0.05;
	}

	 drawNumbers(ctx, radius) {
		let ang;
		let num;
		const circleSize = radius * 0.165;
		const busyColor = "#E0061C";
		const freeColor = "#00CA65";
		for (num = 1; num < 13; num++) {
			ang = (num * Math.PI) / 6;
			ctx.save();
			ctx.rotate(ang);
			ctx.translate(0, -radius * 0.85);

			// Draw the circles

			ctx.beginPath();
			ctx.arc(0, 0, circleSize / 2, 0, 2 * Math.PI);
			ctx.fillStyle = freeColor;
			ctx.fill();
			ctx.closePath();

			// Draw the numbers for 3, 6, 9, and 12
			if (num === 3 || num === 6 || num === 9 || num === 12) {
				ctx.translate(0, -circleSize * 1.3); // Move the text outside the circle
				ctx.rotate(-ang); // Reset rotation for text
				ctx.font = `20px Open Sans`;
				ctx.fillStyle = "#FCF8ED";
				ctx.textAlign = "center";
				ctx.textBaseline = "middle";
				ctx.fillText(num.toString(), 0, 0);
			}

			ctx.restore();
		}
	}

	drawTime(ctx, radius) {
		ctx.strokeStyle = "white"; // Set the color to white

		const { seconds, minutes, hours } = new TDate(new Date());
		const hourHand =
			((hours % 12) * Math.PI) / 6 +
			(minutes * Math.PI) / (6 * 60) +
			(seconds * Math.PI) / (360 * 60);
		this.drawHand(ctx, hourHand, radius * 0.4, radius * 0.05);

		const minuteHand = (minutes * Math.PI) / 30 + (seconds * Math.PI) / (30 * 60);
		this.drawHand(ctx, minuteHand, radius * 0.625, radius * 0.05);

		const secondHand = (seconds * Math.PI) / 30;
		this.drawHand(ctx, secondHand, radius * 0.725, radius * 0.02);
	}

	drawHand(ctx, pos, length, width) {
		ctx.beginPath();
		ctx.lineWidth = width;
		ctx.lineCap = "round";
		ctx.moveTo(0, 0);
		ctx.rotate(pos);
		ctx.lineTo(0, -length);
		ctx.stroke();

		ctx.rotate(-pos);
	}
}

class TDate {
	constructor(readonly date: Date) {}

	get seconds(): number {
		return this.date.getSeconds();
	}

	get minutes(): number {
		return this.date.getMinutes();
	}

	get hours(): number {
		return this.date.getHours();
	}

	get month(): number {
		return this.date.getMonth() + 1;
	}

	get day(): number {
		return this.date.getDate();
	}

	get year(): number {
		return this.date.getFullYear();
	}
}
